import React from "react";
import * as Styled from "./NewBlockForm.styled";
import { useFormik } from "formik";
import { BaseButton, BlockType, IBlockCreateRequest, IPlaylistResponseDto, LayoutTemplates, selectTheme } from "../../../libs";
import { SelectTemplate } from "../select-template";
import { playlistsService } from "../../../services";
import { toast } from "react-toastify";
import { PlayCircle, Tv, Broadcast, List } from "react-bootstrap-icons";
import Select from "react-select";

const BlockTypeOption = [
    { id: BlockType.GENERAL_VOD, label: "General VOD", icon: PlayCircle },
    { id: BlockType.FILM_FESTIVAL_VOD, label: "Film Festival VOD", icon: Tv },
    { id: BlockType.LIVE_STREAMS, label: "Live Streams", icon: Broadcast },
    { id: BlockType.PLAYLIST, label: "Playlist", icon: List },
]

interface NewBlockFormProps {
    show: boolean;
    onClose: () => void;
    onCreateBlock: (data: IBlockCreateRequest) => Promise<void>;
}

export const NewBlockForm: React.FC<NewBlockFormProps> = ({ show, onClose, onCreateBlock }) => {
    const [playlists, setPlaylists] = React.useState<IPlaylistResponseDto[]>([]);

    const fetchPlaylists = React.useCallback(async () => {
        try {
            const data = await playlistsService.getAllPlaylists();
            setPlaylists(data);
        } catch (error) {
            console.error("Error fetching playlists:", error);
            toast.error("Failed to load playlists");
        }
    }, []);

    React.useEffect(() => {
        fetchPlaylists();
    }, [fetchPlaylists]);


    const formik = useFormik({
        initialValues: {
            blockType: BlockType.PLAYLIST,
            enabled: true,
            playlistId: "",
            templateId: LayoutTemplates.Layout1,
        },
        validate: (values) => {
            if (values.blockType === BlockType.PLAYLIST && !values.playlistId) {
                return { playlistId: "Please Select a Playlist" };
            }
        },
        onSubmit: (values) => {
            console.log("Form submitted with values:", values);
            onCreateBlock({
                type: values.blockType,
                enabled: values.enabled,
                renderInfo: { templateId: values.templateId },
                ...(values.blockType === BlockType.PLAYLIST && { payload: { playlistId: values.playlistId } }),
            });
        },
    });

    const { resetForm } = formik;

    React.useEffect(() => {
        if (show) {
            resetForm();
        }
    }, [show, resetForm]);



    return (
        <>
            <Styled.NewBlockModal
                show={show}
                centered
                dialogClassName="modal-width"
                onHide={onClose}
            >
                <Styled.NewBlockModalHeader>
                    <Styled.NewBlockModalTitle>Create New Block</Styled.NewBlockModalTitle>
                    <Styled.CloseButton onClick={onClose} size={26} />
                </Styled.NewBlockModalHeader>

                <Styled.NewBlockModalBody>
                    <Styled.Spacer />
                    <Styled.BlockTypeLabel>Block Type</Styled.BlockTypeLabel>
                    <Styled.BlockTypeList>
                        {BlockTypeOption.map((option) => {
                            const IconComponent = option.icon;
                            return (
                                <Styled.BlockTypeOption
                                    key={option.id}
                                    className={formik.values.blockType === option.id ? "selected" : ""}
                                >
                                    <input
                                        type="radio"
                                        id={`blockType-${option.id}`}
                                        name="blockType"
                                        value={option.id}
                                        checked={formik.values.blockType === option.id}
                                        onChange={(e) => formik.setFieldValue('blockType', Number(e.target.value))}
                                    />
                                    <label htmlFor={`blockType-${option.id}`}>
                                        <Styled.RadioCircle />
                                        <Styled.OptionLabel>{option.label}</Styled.OptionLabel>
                                        <Styled.OptionIcon>
                                            <IconComponent size={20} />
                                        </Styled.OptionIcon>
                                    </label>
                                </Styled.BlockTypeOption>
                            );
                        })}
                    </Styled.BlockTypeList>
                    <Styled.Spacer />
                    <SelectTemplate
                        show={true}
                        template={formik.values.templateId}
                        blockType={formik.values.blockType}
                        onTemplateChange={(templateId) => formik.setFieldValue('templateId', templateId)}
                    />
                    <Styled.SelectPlaylistWrapper $isVisible={formik.values.blockType === BlockType.PLAYLIST}>
                        <Styled.Spacer />
                        <Styled.BlockTypeLabel>Select Playlist</Styled.BlockTypeLabel>
                        <Styled.ErrorText>{formik.errors.playlistId && formik.touched.playlistId ? formik.errors.playlistId : ""}</Styled.ErrorText>
                        <Select
                            className="custom-select"
                            styles={selectTheme.singleSelect()}
                            placeholder="Choose a playlist"
                            value={playlists.find((p) => p.playlistId === formik.values.playlistId) ? {
                                value: formik.values.playlistId,
                                label: playlists.find((p) => p.playlistId === formik.values.playlistId)?.title,
                            } : null}
                            isClearable
                            options={playlists.map((playlist) => ({
                                value: playlist.playlistId,
                                label: playlist.title,
                            }))}
                            onChange={(selectedOption) => {
                                formik.setFieldValue(
                                    "playlistId",
                                    selectedOption?.value || "",
                                );
                            }}
                        />
                    </Styled.SelectPlaylistWrapper>
                    <Styled.Spacer />
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <BaseButton
                            variant="outline-secondary"
                            onClick={onClose}
                        >
                            Cancel
                        </BaseButton>
                        <BaseButton
                            variant="primary"
                            onClick={() => formik.handleSubmit()}
                        >
                            Create Block
                        </BaseButton>
                    </div>
                </Styled.NewBlockModalBody>
            </Styled.NewBlockModal>
        </>
    )
};